import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

/**
 * One QueryClient per app instance, held via `useState` so React
 * StrictMode's double-mount doesn't recreate it (and lose the cache).
 *
 * Defaults are tuned for a kids' studio: aggressive cache reuse on
 * navigation, no automatic refetch on window-focus (kids alt-tab a
 * lot — they don't expect a refetch every time).
 */
export function ReactQueryProvider({ children }: { children: ReactNode }): JSX.Element {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              if (error instanceof Error && /^Auth/.test(error.name)) return false;
              return failureCount < 2;
            },
          },
          mutations: { retry: false },
        },
      }),
  );
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
